'use client';

import { useState } from 'react';
import { Badge, Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import useServer from '@/util/hooks/useServer';
import NotificationsList from '@/components/dashboard/RecentMentions/MentionsList';
import { Notification } from '@/util/types/serverTypeCollection/activity';
import { FaBell } from 'react-icons/fa6';
import { useGlobalContext } from '@/app/tools/globalContext';
import { useClientMounted } from '@/util/hooks/useClientMounted';

type NotificationsResponse = {
  items: Notification[];
  total: number;
  unreadCount: number;
};

type CountResponse = {
  unreadCount: number;
};

export default function NavNotifications() {
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { mobileMenuOpen, setMobileMenuOpen } = useGlobalContext();

  // Always fetch count (fast)
  const { data: countData, mutate: mutateCount } = useServer<CountResponse>(
    {
      url: 'notification/count',
    },
    {
      dedupingInterval: 60000, // 1 min deduping
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );

  // Only fetch full list when popover is open
  const {
    data: notificationsData,
    loading,
    mutate: mutateList,
  } = useServer<NotificationsResponse>(
    isOpen
      ? {
        url: 'notification',
        params: {
          unreadOnly: showAll ? 'false' : 'true',
          limit: 10,
        },
      }
      : null
  );

  // console.log(notificationsData);

  const unreadCount = countData?.unreadCount ?? 0;
  const mounted = useClientMounted();

  if (!mounted) {
    return (
      <div className='group/item hover:text-white'>
        <div className='flex w-full cursor-default items-center justify-start whitespace-nowrap py-1 outline-none'>
          <div className='flex items-center justify-center group-hover/navbar:justify-start'>
            <div className='relative px-2 text-[1.2em] group-hover/item:animate-shake-rotate'>
              <FaBell />
              {unreadCount > 0 ? (
                <span className='absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white shadow-sm'>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </div>
            <p
              className={`opacity-0 transition-opacity group-hover/navbar:opacity-100 ${mobileMenuOpen ? 'block! opacity-100!' : ''}`}
            >
              Notifications
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='group/item hover:text-white'>
      <Popover
        isOpen={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (open) {
            setMobileMenuOpen(false);
          }
        }}
        placement='right'
        classNames={{
          content: 'p-0',
        }}
      >
        <PopoverTrigger>
          <button className='cursor-pointer flex w-full items-center justify-start whitespace-nowrap py-1 outline-none'>
            <div className='flex items-center justify-center group-hover/navbar:justify-start'>
              <div className='px-2 text-[1.2em] group-hover/item:animate-shake-rotate'>
                <Badge
                  content={unreadCount > 0 ? unreadCount : null}
                  color='danger'
                  size='sm'
                  isInvisible={unreadCount === 0}
                  variant='shadow'
                  showOutline={false}
                >
                  <FaBell />
                </Badge>
              </div>
              <p className={`opacity-0 transition-opacity group-hover/navbar:opacity-100 ${mobileMenuOpen ? 'block! opacity-100!' : ''}`}>
                Notifications
              </p>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent>
          <div className='w-96 p-2'>
            <NotificationsList
              data={notificationsData?.items}
              loading={loading}
              mutate={mutateList}
              onCountChange={mutateCount}
              emptyMessage={
                unreadCount === 0 && !showAll ? '🎉  All caught up!' : 'No notifications'
              }
            />
            {!loading && (
              <div className='mt-2 flex justify-center border-t border-gray-100 pt-2'>
                {!showAll ? (
                  <button
                    onClick={() => setShowAll(true)}
                    className='cursor-pointer uppercase font-bold text-xs text-gray-500 hover:text-primary transition-colors'
                  >
                    View All notifications
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAll(false)}
                    className='cursor-pointer uppercase font-bold text-xs text-gray-500 hover:text-primary transition-colors'
                  >
                    {'<'} Back to unread
                  </button>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
